import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { FileUploadZone } from './upload/FileUploadZone';
import { ValidationErrors } from './upload/ValidationErrors';
import { WarningMessage } from './upload/WarningMessage';
import { useGuidesStore } from '../../stores/guidesStore';
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { GuideSearch } from './modal/GuideSearch';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const BulkLinkModal = ({ isOpen, onClose }: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [selectedGuideId, setSelectedGuideId] = useState('');
  const { translations } = useLanguage();
  const { guides } = useGuidesStore();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();

  const handleDownloadTemplate = () => {
    const template = [
      {
        identifier: '123456789',
        identifier_type: 'passport', // Only passport is supported
        is_owner: 'true' // New field for ownership status
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'bulk-link-template.xlsx');
  };

  const handleUpload = () => {
    if (!file) return;
    if (userRole === 'admin' && !selectedGuideId) {
      toast.error(
        translations?.bulkLink?.validation?.guideRequired ||
          'Please select a guide before uploading.'
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      if (data) {
        // Parse Excel file to check for email entries
        const workbook = XLSX.read(data, { type: 'binary' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(worksheet);

        // Check for any email entries
        const hasEmailEntries = rows.some((row: any) => 
          row.identifier_type?.toLowerCase() === 'email'
        );

        if (hasEmailEntries) {
          toast.error(translations?.bulkLink?.emailSearchSuspended || 'Email search is temporarily suspended. Please use passport numbers only.');
          return;
        }

        // Close modal first
        onClose();
        
        // Navigate to status page with file data
        navigate('/bulk-link-status', {
          state: {
            fileData: data,
            selectedGuideId: userRole === 'guide' ? userId : selectedGuideId || undefined,
          },
          replace: true
        });
      }
    };
    reader.readAsBinaryString(file);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={translations?.bulkLink?.title || 'Bulk Link Pilgrims'}
    >
      <div className="space-y-6">
        {/* Email Search Suspended Warning */}
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-yellow-800">
              {translations?.bulkLink?.emailSearchSuspended || 'Email search is temporarily suspended'}
            </span>
          </div>
          <p className="mt-2 text-sm text-yellow-600">
            {translations?.bulkLink?.usePassportOnly || 'Please use passport numbers only for bulk linking.'}
          </p>
        </div>

        {userRole === 'admin' && (
          <GuideSearch
            guides={guides}
            selectedGuideId={selectedGuideId}
            onSelectGuide={setSelectedGuideId}
            userRole={userRole}
            userId={userId || undefined}
          />
        )}

        {/* Template Download */}
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>{translations?.bulkLink?.downloadTemplate || 'Download Excel Template'}</span>
        </button>

        {/* File Upload */}
        <FileUploadZone
          file={file}
          onFileSelect={setFile}
          onFileRemove={() => {
            setFile(null);
            setValidationErrors([]);
          }}
        />

        <ValidationErrors errors={validationErrors} />
        <WarningMessage variant="bulkLink" />

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.bulkLink?.cancel || 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || (userRole === 'admin' && !selectedGuideId)}
            className={`px-6 py-2 rounded-lg text-white
              ${!file || (userRole === 'admin' && !selectedGuideId)
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {translations?.bulkLink?.upload || 'Upload'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
